import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, CheckCircle, Phone, Mail, MapPin, Clock, DollarSign, FileText, User, Building, Calendar, Upload, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MakePayment = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('make-payment'); // 'make-payment' or 'already-paid'
  const [loading, setLoading] = useState(false);

  // Make Payment Form State
  const [paymentForm, setPaymentForm] = useState({
    name: '',
    email: '',
    mobile: '',
    invoiceNumber: '',
    invoiceAmount: '',
    officeName: '',
    address: '',
    service: '',
    paymentMode: ''
  });

  // Already Paid Form State
  const [paidForm, setPaidForm] = useState({
    name: '',
    email: '',
    mobile: '',
    invoiceNumber: '',
    amountPaid: '',
    officeName: '',
    address: '',
    paymentDate: '',
    service: '',
    paymentMode: ''
  });

  // File attachment state
  const [attachments, setAttachments] = useState([]);

  const services = [
    'Internet Lease Line',
    'Enterprise Broadband',
    'Internet Networking',
    'Device Charges',
    'ILL OTC Charges',
    'Service Charge'
  ];

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.invoiceNumber) {
      toast.error('Invoice Number is mandatory!');
      return;
    }

    if (!paymentForm.name || !paymentForm.mobile) {
      toast.error('Please fill all required fields');
      return;
    }

    // Navigate to payment confirmation page with payment data
    navigate('/payment-confirmation', { state: paymentForm });
  };

  const handleAlreadyPaidSubmit = async (e) => {
    e.preventDefault();
    
    if (!paidForm.invoiceNumber || !paidForm.mobile) {
      toast.error('Invoice Number and Mobile Number are mandatory!');
      return;
    }

    if (!paidForm.name || !paidForm.amountPaid || !paidForm.paymentDate) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!paidForm.email || !paidForm.email.includes('@')) {
      toast.error('Valid email address is required');
      return;
    }

    setLoading(true);

    try {
      // Prepare form data for backend API
      const formData = new FormData();
      formData.append('transactionId', 'Already Paid - Awaiting Verification');
      formData.append('paidAmount', paidForm.amountPaid);
      formData.append('invoiceNumber', paidForm.invoiceNumber);
      formData.append('paymentDate', paidForm.paymentDate);
      formData.append('customerName', paidForm.name);
      formData.append('customerMobile', paidForm.mobile);
      formData.append('customerEmail', paidForm.email);
      formData.append('officeName', paidForm.officeName || 'N/A');
      formData.append('service', paidForm.service);
      formData.append('address', paidForm.address || 'N/A');
      formData.append('paymentMode', paidForm.paymentMode);
      
      // Append attachment files
      if (attachments.length > 0) {
        attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }

      // Send to backend
      const response = await fetch(`${API_BASE_URL}/api/payments/send-confirmation-email`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('✅ Payment confirmation email sent successfully!');
        toast.info(`📧 Email sent from ${data.sent_from} to ${data.sent_to}`);
        toast.info(`📨 Confirmation copy sent to your email: ${paidForm.email}`);
        
        // Reset form
        setPaidForm({
          name: '',
          email: '',
          mobile: '',
          invoiceNumber: '',
          amountPaid: '',
          officeName: '',
          address: '',
          paymentDate: '',
          service: '',
          paymentMode: ''
        });
        setAttachments([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send payment confirmation email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Error sending payment confirmation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Beautiful Header */}
      <div className="bg-gradient-to-r from-red-600 via-yellow-500 to-gray-400 text-white py-12 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-3 drop-shadow-lg">Make Payment</h1>
            <p className="text-xl text-red-50">Fast, Secure & Convenient Payment Solutions</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4">
                <div className="bg-red-600 p-3 rounded-full">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Quick Payment</h3>
                  <p className="text-sm text-red-50">Pay your bills instantly</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4">
                <div className="bg-yellow-600 p-3 rounded-full">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Secure Transaction</h3>
                  <p className="text-sm text-red-50">100% safe & encrypted</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4">
                <div className="bg-gray-600 p-3 rounded-full">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Instant Receipt</h3>
                  <p className="text-sm text-red-50">Get confirmation instantly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-r from-red-50 to-yellow-50 border-2 border-red-200 rounded-2xl p-8 shadow-xl mb-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">NETIX WORLD INTERNET SERVICES (P) LIMITED</h2>
            <p className="text-gray-700 text-lg">Your Trusted High-Speed Internet Service Provider</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-md border border-red-100">
              <Phone className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Customer Support</p>
                <p className="text-sm text-gray-700" data-testid="payment-support-mobile">+91 93199 19988</p>
                <p className="text-sm text-gray-700" data-testid="payment-office-phone">Office Phone - 06276 - 459015</p>
                <p className="text-sm text-gray-700">24/7 Available</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-md border border-yellow-100">
              <Mail className="h-5 w-5 text-yellow-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Email Support</p>
                <p className="text-sm text-gray-700">care@netixfibernet.com</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-md border border-gray-200">
              <MapPin className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Service Areas</p>
                <p className="text-sm text-gray-700">Delhi, Haryana</p>
                <p className="text-sm text-gray-700">Bihar, Uttar Pradesh</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-4 mb-8 max-w-2xl mx-auto">
          <button
            onClick={() => setActiveTab('make-payment')}
            className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg transition-all shadow-lg ${
              activeTab === 'make-payment'
                ? 'bg-gradient-to-r from-red-600 to-red-500 text-white scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
            }`}
          >
            <CreditCard className="inline h-5 w-5 mr-2" />
            Make Payment
          </button>
          <button
            onClick={() => setActiveTab('already-paid')}
            className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg transition-all shadow-lg ${
              activeTab === 'already-paid'
                ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-white scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
            }`}
          >
            <CheckCircle className="inline h-5 w-5 mr-2" />
            Already Paid
          </button>
        </div>

        {/* Make Payment Form */}
        {activeTab === 'make-payment' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl border-2 border-red-200 overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-red-500 text-white p-6">
                <h2 className="text-3xl font-bold flex items-center gap-3">
                  <CreditCard className="h-8 w-8" />
                  Make Payment
                </h2>
                <p className="text-red-50 mt-2">Complete the form below to proceed with payment</p>
              </div>
              
              <form onSubmit={handlePaymentSubmit} className="p-8 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <User className="inline h-4 w-4 mr-2 text-red-600" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={paymentForm.name}
                      onChange={(e) => setPaymentForm({...paymentForm, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Phone className="inline h-4 w-4 mr-2 text-red-600" />
                      Registered Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={paymentForm.mobile}
                      onChange={(e) => setPaymentForm({...paymentForm, mobile: e.target.value})}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all"
                      placeholder="10-digit mobile number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Mail className="inline h-4 w-4 mr-2 text-red-600" />
                    Email Address <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    value={paymentForm.email}
                    onChange={(e) => setPaymentForm({...paymentForm, email: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-red-300 rounded-xl focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all bg-red-50"
                    placeholder="your.email@example.com"
                  />
                  <p className="text-xs text-red-600 mt-1 font-semibold">Confirmation email will be sent to this address</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <FileText className="inline h-4 w-4 mr-2 text-red-600" />
                      Invoice Number <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={paymentForm.invoiceNumber}
                      onChange={(e) => setPaymentForm({...paymentForm, invoiceNumber: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-red-300 rounded-xl focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all bg-red-50"
                      placeholder="INV202500001"
                    />
                    <p className="text-xs text-red-600 mt-1 font-semibold">Required field</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <DollarSign className="inline h-4 w-4 mr-2 text-yellow-600" />
                      Invoice Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={paymentForm.invoiceAmount}
                      onChange={(e) => setPaymentForm({...paymentForm, invoiceAmount: e.target.value})}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all"
                      placeholder="Enter amount"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Building className="inline h-4 w-4 mr-2 text-gray-600" />
                      Office Name
                    </label>
                    <input
                      type="text"
                      value={paymentForm.officeName}
                      onChange={(e) => setPaymentForm({...paymentForm, officeName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all"
                      placeholder="Your office name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <MapPin className="inline h-4 w-4 mr-2 text-gray-600" />
                      Select Service
                    </label>
                    <select
                      value={paymentForm.service}
                      onChange={(e) => setPaymentForm({...paymentForm, service: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all"
                    >
                      <option value="">Select a service</option>
                      {services.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <CreditCard className="inline h-4 w-4 mr-2 text-red-600" />
                    Payment Mode <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={paymentForm.paymentMode}
                    onChange={(e) => setPaymentForm({...paymentForm, paymentMode: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-red-300 rounded-xl focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all bg-red-50"
                  >
                    <option value="">Select payment mode</option>
                    <option value="UPI">UPI</option>
                    <option value="QR Code">QR Code</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Bank Transfer">Bank Transfer / NEFT / RTGS</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                  <p className="text-xs text-red-600 mt-1 font-semibold">Required - Select how you will pay</p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-2 text-gray-600" />
                    Address
                  </label>
                  <textarea
                    value={paymentForm.address}
                    onChange={(e) => setPaymentForm({...paymentForm, address: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all resize-none"
                    placeholder="Enter your complete address"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-bold py-4 px-6 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                >
                  {loading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="inline h-5 w-5 mr-2" />
                      Proceed to Make Payment
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Already Paid Form */}
        {activeTab === 'already-paid' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl border-2 border-yellow-200 overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-white p-6">
                <h2 className="text-3xl font-bold flex items-center gap-3">
                  <CheckCircle className="h-8 w-8" />
                  Already Paid Submission
                </h2>
                <p className="text-yellow-50 mt-2">Submit your payment details for verification</p>
              </div>
              
              <form onSubmit={handleAlreadyPaidSubmit} className="p-8 space-y-6">
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6">
                  <p className="text-yellow-800 font-semibold">
                    <CheckCircle className="inline h-5 w-5 mr-2" />
                    Submit your payment details here if you have already made the payment through other channels.
                    Our team will verify and update your account within 24 hours.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <User className="inline h-4 w-4 mr-2 text-yellow-600" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={paidForm.name}
                      onChange={(e) => setPaidForm({...paidForm, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Phone className="inline h-4 w-4 mr-2 text-yellow-600" />
                      Registered Mobile Number <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="tel"
                      value={paidForm.mobile}
                      onChange={(e) => setPaidForm({...paidForm, mobile: e.target.value})}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all bg-yellow-50"
                      placeholder="10-digit mobile number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Mail className="inline h-4 w-4 mr-2 text-yellow-600" />
                    Email Address <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    value={paidForm.email}
                    onChange={(e) => setPaidForm({...paidForm, email: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all bg-yellow-50"
                    placeholder="your.email@example.com"
                  />
                  <p className="text-xs text-yellow-700 mt-1 font-semibold">Confirmation email will be sent to this address</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <FileText className="inline h-4 w-4 mr-2 text-yellow-600" />
                      Invoice Number <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={paidForm.invoiceNumber}
                      onChange={(e) => setPaidForm({...paidForm, invoiceNumber: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all bg-yellow-50"
                      placeholder="INV202500001"
                    />
                    <p className="text-xs text-red-600 mt-1 font-semibold">Required field</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <DollarSign className="inline h-4 w-4 mr-2 text-yellow-600" />
                      Total Amount Paid (₹)
                    </label>
                    <input
                      type="number"
                      value={paidForm.amountPaid}
                      onChange={(e) => setPaidForm({...paidForm, amountPaid: e.target.value})}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all"
                      placeholder="Enter paid amount"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Building className="inline h-4 w-4 mr-2 text-gray-600" />
                      Office Name
                    </label>
                    <input
                      type="text"
                      value={paidForm.officeName}
                      onChange={(e) => setPaidForm({...paidForm, officeName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all"
                      placeholder="Your office name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-2 text-gray-600" />
                      Payment Date
                    </label>
                    <input
                      type="date"
                      value={paidForm.paymentDate}
                      onChange={(e) => setPaidForm({...paidForm, paymentDate: e.target.value})}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-2 text-gray-600" />
                    Select Service
                  </label>
                  <select
                    value={paidForm.service}
                    onChange={(e) => setPaidForm({...paidForm, service: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all"
                  >
                    <option value="">Select a service</option>
                    {services.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <CreditCard className="inline h-4 w-4 mr-2 text-yellow-600" />
                    Payment Mode <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={paidForm.paymentMode}
                    onChange={(e) => setPaidForm({...paidForm, paymentMode: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all bg-yellow-50"
                  >
                    <option value="">Select payment mode</option>
                    <option value="UPI">UPI</option>
                    <option value="QR Code">QR Code</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Bank Transfer">Bank Transfer / NEFT / RTGS</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                  <p className="text-xs text-yellow-700 mt-1 font-semibold">Required - How did you pay?</p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-2 text-gray-600" />
                    Address
                  </label>
                  <textarea
                    value={paidForm.address}
                    onChange={(e) => setPaidForm({...paidForm, address: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all resize-none"
                    placeholder="Enter your complete address"
                  ></textarea>
                </div>

                {/* Payment Proof Attachment */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Paperclip className="inline h-4 w-4 mr-2 text-yellow-600" />
                    Attach Payment Proof (Optional)
                  </label>
                  <div className="border-2 border-dashed border-yellow-300 rounded-xl p-6 bg-yellow-50 hover:bg-yellow-100 transition-all">
                    <input
                      type="file"
                      id="payment-attachments"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setAttachments(files);
                        if (files.length > 0) {
                          toast.success(`${files.length} file(s) selected`);
                        }
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="payment-attachments"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="h-12 w-12 text-yellow-600 mb-3" />
                      <span className="text-sm font-semibold text-gray-700 mb-1">
                        Click to upload payment receipt
                      </span>
                      <span className="text-xs text-gray-500">
                        Cheque copy, transaction screenshot, or payment receipt
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        Supports: Images, PDF, Word documents (Max 10MB per file)
                      </span>
                    </label>
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-white border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-yellow-600" />
                            <span className="text-sm font-medium text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newFiles = attachments.filter((_, i) => i !== index);
                              setAttachments(newFiles);
                              toast.info('File removed');
                            }}
                            className="text-red-600 hover:text-red-800 text-sm font-semibold"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-yellow-700 mt-2 font-semibold flex items-center gap-1">
                    <CheckCircle className="inline h-3 w-3" />
                    Attach cheque copy, bank transfer receipt, or payment confirmation
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-700 hover:to-yellow-600 text-white font-bold py-4 px-6 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                >
                  {loading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="inline h-5 w-5 mr-2" />
                      Submit Payment Details
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="max-w-4xl mx-auto mt-8 bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-300 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Important Information</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <span>All transactions are secured with 256-bit SSL encryption</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Payment confirmation will be sent to your registered email and mobile</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <span data-testid="payment-query-contact">For payment queries, contact care@netixfibernet.com, call +91 93199 19988, or Office Phone 06276 - 459015</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Already paid submissions will be verified within 24 hours</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MakePayment;
